import { IconCheckDefault, IconExclamationTriangleOutline } from '@iwoca/icons';
import { Card } from '@iwoca/orion';
import clsx from 'clsx';
import { TFile } from 'components/Signup/hooks/useFileUploadReducer';
import { UploadedFiles } from 'components/UploadedFiles/UploadedFiles';
import FileUpIcon from 'svg/FileUpIcon';
import { TFileStatusGroup } from 'types';
import { TDocRequirementReview } from '../DocumentGroup/DocumentGroup';
import styles from './FileStatusGroup.module.css';
import { renderDocumentText } from './FileStatusGroupHelper';
import { MissingCoverages } from './MissingCoverages/MissingCoverages';

type TProps = {
  files: TFile[];
  statusGroup: TFileStatusGroup;
  removeFile: (id: string) => void;
  testId?: string;
  missingCoverages?: TDocRequirementReview['bank_account_coverages'];
};

export const FileStatusGroup = ({
  files,
  statusGroup,
  removeFile,
  testId,
  missingCoverages,
}: TProps) => {
  const filesToDisplay = files.filter((file) => {
    return file.statusGroup === statusGroup;
  });

  const renderStatusIcon = () => {
    switch (statusGroup) {
      case 'uploaded':
        return (
          <FileUpIcon className={styles.icon} data-testid="uploaded-icon" />
        );
      case 'approved':
        return (
          <IconCheckDefault
            data-testid="approved-icon"
            className={styles.icon}
          />
        );
      case 'overridden':
        return (
          <IconExclamationTriangleOutline
            data-testid="overridden-icon"
            className={styles.icon}
          />
        );
      case 'rejected':
      default:
        return (
          <IconExclamationTriangleOutline
            data-testid="rejected-icon"
            className={styles.icon}
          />
        );
    }
  };

  const renderStatusMessage = () => {
    return (
      <div className={styles.status}>
        {renderStatusIcon()}

        <p className={styles.statusText}>
          {renderDocumentText({ files: filesToDisplay, statusGroup })}
        </p>
      </div>
    );
  };

  const hasMissingCoverages = missingCoverages && missingCoverages.length > 0;

  if (filesToDisplay.length === 0 && !hasMissingCoverages) {
    return null;
  }

  const dataTestId = `status-group${testId ? `-${testId}` : ''}`;

  return (
    <Card
      data-testid={dataTestId}
      className={clsx(styles.statusGroup, styles[statusGroup])}
    >
      <div className={styles.fileList}>
        <UploadedFiles files={filesToDisplay} removeFile={removeFile} />
        {hasMissingCoverages && (
          <MissingCoverages
            coverages={missingCoverages}
            statusGroup={statusGroup}
          />
        )}
      </div>
      {renderStatusMessage()}
    </Card>
  );
};
